import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import _toNumber from 'lodash/toNumber';
import { connect } from 'react-redux';
import {
  shouldHideSidePanel,
  getToolIds,
  GRADEBOOK_SETTINGS,
} from '@macmillan-learning/gradebook-shared';
import s from './GridCell.module.css';
import {
  getCategoryRowData,
  getAssignmentRowData,
  getAverageRowData,
  getStudentRowData,
} from './GridCellDataHelpers';
import {
  CATEGORY_ROW,
  ASSIGNMENT_ROW,
  AVERAGE_ROW,
  STUDENT_ROW,
  FIRST_COLUMN,
} from '../../../../utils/constants';
import { GRADING_TYPE } from '../../../../utils/constants';
import {
  createIndividualStudentGradebookUrl,
  getAssignedPoints,
} from '../../../../utils/InstructorGradebookHelpers';
import CategoryNameButton from '../../../../components/CategoryNameButton';
import GridCellBorder from '../GridCellBorder/GridCellBorder';
import MLIcon from '../../../MLIcon/MLIcon';
import {
  getGradebook,
  updateGradebook,
  toggleCategoryExpandCollapse,
  openIndividualStudentGradebook,
} from '../../../../actions/gradebook';
import {
  getNewStateSortOptions,
  getSortProperties,
} from '../../../../actions/getGradebook';
import { sendAnalyticsEvent } from '../../../../actions/sendAnalytics';
import {
  clickStudentGrade,
  clickStudentName,
} from '../../../../actions/analyticsEvents';
import { getConfigObject } from '../../../../utils/config';
import useSidePanel from '../../../SidePanel/hooks/useSidePanel';
import {
  CLOSE_SIDE_PANEL,
  SET_PANEL_PARAMS,
} from '../../../SidePanel/store/actions';
import MLTooltip from '../../../MLTooltip';

const GridCell = ({
  style,
  dispatch,
  columnIndex,
  rowIndex,
  scrollToColumn,
  scrollToRow,
  cellData,
  collapsedCategoryIds,
  showPercent,
  onSelectCell,
  leftGridColumnCount,
  handleCellHover,
  hoveredColumnIndex,
  hoveredRowIndex,
  showGradeOverrideDialog,
  gradingType,
  roundPercentType,
  user,
  studentSortKey,
  studentSortDirection,
  courseId,
  sortColumnHistory,
  getLaunchUrl,
  handleLocalLaunch,
  hasStudentReport,
  getStudentReportLink,
  categories,
}) => {
  const { FF_SIDE_PANEL_ACTIVE } = getConfigObject();
  let content = '';
  let testId = '';
  let containerStyles = '';
  let isGradeCell = false;
  let isAssignmentHeaderCell = false;
  let isCellExpandable = false;
  let hasDueDateException = false;
  const isSingleCell = get(cellData, 'isSingleCell', false);
  const isAggregateCell = get(cellData, 'isAggregateCell', false);
  const isAssigned = get(cellData, 'isAssigned', false);
  const studentId = get(cellData, 'studentId', '');
  const assignmentId = get(cellData, 'id', '');
  let receivedLatePenalty = get(cellData, 'receivedLatePenalty', false);
  const isOverdue = get(cellData, 'isOverdue', false);
  const shouldSidePanelBeActive =
    FF_SIDE_PANEL_ACTIVE && !shouldHideSidePanel(cellData.tool);
  const shouldCloseSidePanel =
    FF_SIDE_PANEL_ACTIVE && shouldHideSidePanel(cellData.tool);

  const gradebookCategoryId = get(cellData, 'gradebook_category_id', 0);
  const categoryFromRedux = categories.find(
    (category) => category.id === gradebookCategoryId,
  );
  const isUngradedHeaderColumn = !categoryFromRedux
    ? gradebookCategoryId === -1
    : !!categoryFromRedux.is_default_ungraded;
  const isCollapsed = collapsedCategoryIds.includes(
    cellData.gradebook_category_id,
  );
  const focusedClass =
    columnIndex === scrollToColumn && rowIndex === scrollToRow
      ? s.focusedCell
      : '';
  const isCourseTotalColumn = get(cellData, 'isCourseTotalColumn', false);
  const cellId = isCourseTotalColumn ? 'course_total' : cellData.id;

  const { state: sidePanelState, dispatch: sidePanelDispatch } = useSidePanel();

  const onChangeSort = (newSortDirection) => {
    const newState = getNewStateSortOptions(
      cellId,
      newSortDirection,
      sortColumnHistory,
    );

    const sortOptions = {
      studentSortDirection: newState.studentSortDirection,
      iconSortDirection: newState.studentSortDirection,
      studentSortKey: newState.studentSortKey,
      page: 1,
    };

    dispatch(getGradebook(courseId, sortOptions));
    dispatch(
      updateGradebook({
        page: 1,
        sortColumn: newState.sortColumn,
        sortColumnHistory: newState.history,
        sortDirection: newState.studentSortDirection,
      }),
    );
  };

  const { GRADED, EXTRA_CREDIT } = GRADING_TYPE;
  const { manual: manualToolId } = getToolIds();
  if (
    isUngradedHeaderColumn || // show sorter in ungraded assignments
    isCourseTotalColumn || // show sorter in course total
    (cellData.tool === manualToolId &&
      [GRADED, EXTRA_CREDIT].includes(cellData.grading_type)) || // show sorter in manual assignment
    (_toNumber(cellData.id) === cellData.gradebook_category_id &&
      !isUngradedHeaderColumn) || // show sorter in category total
    (cellData.tool !== manualToolId &&
      [GRADED, EXTRA_CREDIT].includes(cellData.grading_type)) // show sorter in graded assignments
  ) {
    cellData.includeSorter = true;
  }

  switch (rowIndex) {
    case CATEGORY_ROW:
      ({ content, containerStyles, testId, isCellExpandable } =
        getCategoryRowData({
          cellData,
          columnIndex,
          isCollapsed,
          leftGridColumnCount,
          gradingType,
          roundPercentType,
        }));
      break;
    case ASSIGNMENT_ROW:
      ({ content, containerStyles, testId, isAssignmentHeaderCell } =
        getAssignmentRowData({
          cellData,
          columnIndex,
          rowIndex,
          leftGridColumnCount,
          hoveredRowIndex,
          hoveredColumnIndex,
          user,
          getLaunchUrl,
          handleLocalLaunch,
          shouldSidePanelBeActive,
          selectedCell: sidePanelState.selectedCell,
          sortOptions: cellData.includeSorter
            ? {
                active: studentSortKey && studentSortKey === cellId,
                direction: studentSortDirection,
                onChangeSort,
              }
            : undefined,
        }));
      break;
    case AVERAGE_ROW:
      ({ content, containerStyles, testId } = getAverageRowData({
        cellData,
        showPercent,
        roundPercentType,
        columnIndex,
        hoveredRowIndex,
        hoveredColumnIndex,
      }));
      break;
    default:
      ({ content, testId, containerStyles, isGradeCell, hasDueDateException } =
        getStudentRowData({
          cellData,
          showPercent,
          roundPercentType,
          columnIndex,
          rowIndex,
          hoveredRowIndex,
          hoveredColumnIndex,
        }));
      break;
  }

  // Highlights the clicked cell and collapses/expands
  // the category if it's a category cell
  const handleSelectClick = (event) => {
    if (isCellExpandable) {
      dispatch(toggleCategoryExpandCollapse(cellData.gradebook_category_id));
    }

    if (
      !isAggregateCell &&
      !isCourseTotalColumn &&
      assignmentId !== '' &&
      (isAssignmentHeaderCell || (isGradeCell && isAssigned))
    ) {
      const { testId = '' } = event?.target?.dataset ?? {};
      const isSortEvent = testId === 'gradebook-sort-assignmentSorter';

      if (shouldSidePanelBeActive && !isSortEvent) {
        const isgUrl = createIndividualStudentGradebookUrl({
          studentId: cellData.studentId,
          showPercent,
        });

        const isAssessment = cellData.tool === getToolIds().assessment;

        sidePanelDispatch({
          type: SET_PANEL_PARAMS,
          payload: {
            resourceId: cellData.id,
            studentId: isGradeCell ? cellData.studentId : '',
            hasResponses: isAssessment && cellData.hasLaunched,
            type: isGradeCell ? 'student' : 'assignment',
            actualCourseId: cellData.course_id,
            isgUrl,
            courseId,
            open: true,
          },
        });
      }

      if (shouldCloseSidePanel && sidePanelState.open) {
        sidePanelDispatch({ type: CLOSE_SIDE_PANEL });
      }
    }

    return onSelectCell({
      scrollToColumn: isCellExpandable ? -1 : columnIndex,
      scrollToRow: isCellExpandable ? -1 : rowIndex,
    });
  };

  const handleEditGradeClick = () => {
    if (FF_SIDE_PANEL_ACTIVE && !shouldHideSidePanel(cellData.tool)) {
      return;
    }
    let studentAssignmentLink;
    const tool = cellData && cellData.tool;
    const hasLaunched = cellData && cellData.hasLaunched;
    const supportsUserAssignmentLinks =
      tool === getToolIds().assessment || tool === getToolIds().goreact;

    if (supportsUserAssignmentLinks && hasLaunched) {
      // This will provide the grade modal with a link to view the student assignment
      const activityLink = getLaunchUrl({
        ...cellData,
        actual_course_id: cellData.course_id,
      });
      studentAssignmentLink = `${activityLink}&studentUserId=${cellData.studentId}`;
    }
    if (!cellData.isUngraded) {
      const studentReportLink = getStudentReportLink(studentId, assignmentId);
      showGradeOverrideDialog({
        hasStudentReport,
        studentReportLink,
        userId: cellData.studentId,
        userName: cellData.studentName,
        assignmentId: cellData.id,
        assignmentName: cellData.name,
        studentPoints: cellData.studentPoints,
        studentGrade: cellData.studentGrade,
        subtype: cellData.subtype,
        tool: cellData.tool,
        assignedPoints: getAssignedPoints(cellData),
        dueDate: cellData.studentAssignmentDueAt,
        gradeComment: cellData.gradeComment,
        dateCompleted: cellData.dateCompleted,
        isAssigned: cellData.isAssigned,
        isInstructorEntered: cellData.isInstructorEntered,
        grading_type: cellData.grading_type,
        studentAssignmentLink,
        status: cellData.status,
        isOverdue,
      });
    }

    handleSelectClick();
    dispatch(sendAnalyticsEvent(clickStudentGrade(courseId)));
  };

  const handleHover = () => {
    handleCellHover({
      hoveredColumnIndex: isGradeCell ? columnIndex : null,
      hoveredRowIndex: isGradeCell ? rowIndex : null,
    });
  };

  const buttonWrapper = (children) => {
    if (isCellExpandable) {
      return (
        <CategoryNameButton
          showWeightLabel={
            gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted
          }
          title={content}
          testId={'instructor-view-collapse-gradebook-category'}
          category={cellData}
          active={isCollapsed}
          isDisabled={isSingleCell}
          handleCategoryTabToggle={handleSelectClick}
        />
      );
    }

    // Only editable grades
    if (
      isGradeCell &&
      !isAggregateCell &&
      isAssigned &&
      !isUngradedHeaderColumn
    ) {
      return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className={s.gradeContainer} onClick={handleSelectClick}>
          {receivedLatePenalty && !isOverdue && (
            <span className={s.lateLabel}> Late </span>
          )}
          {isOverdue && <span className={s.lateLabel}> Overdue </span>}
          <button
            className={s.gradeButton}
            onClick={handleEditGradeClick}
            data-grid-id={`row${rowIndex}-col${columnIndex}`}
          >
            {children}
          </button>
        </div>
      );
    }

    // Non-editable grades
    if (
      isGradeCell &&
      (isAggregateCell || !isAssigned || isUngradedHeaderColumn)
    ) {
      return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className={s.gradeContainer} onClick={handleSelectClick}>
          {children}
        </div>
      );
    }

    // Everything else
    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        className={classNames(s.wrapper, {
          [s.studentNamewrapper]: cellData.isStudentNameColumn,
          [s.buttonWrapper]: shouldSidePanelBeActive,
        })}
        onClick={handleSelectClick}
        data-test-id={`row${rowIndex}-col${columnIndex}`}
      >
        {children}
      </div>
    );
  };

  let buttonProps;
  let cellStyles = style;

  if (rowIndex >= STUDENT_ROW && columnIndex === FIRST_COLUMN) {
    buttonProps = {
      onClick: () => {
        dispatch(openIndividualStudentGradebook(studentId));
        dispatch(sendAnalyticsEvent(clickStudentName(courseId)));
      },
      tabIndex: '0',
    };
    cellStyles = {
      ...cellStyles,
      cursor: 'pointer',
    };
  }

  const isAssignmentSelected =
    isAssignmentHeaderCell &&
    sidePanelState.resourceId === cellData.id &&
    sidePanelState.studentId === '';

  const isStudentSelected =
    isGradeCell &&
    sidePanelState.resourceId === cellData.id &&
    sidePanelState.studentId === cellData.studentId;

  const isCellSelected =
    shouldSidePanelBeActive && (isAssignmentSelected || isStudentSelected);

  // Determine if this is a header cell
  const isHeader = rowIndex < 3; // First three rows are headers
  const cellRole = isHeader ? 'columnheader' : 'gridcell';

  return (
    <div
      role={cellRole}
      style={cellStyles}
      data-test-id={testId}
      data-grid-id={`r${rowIndex}-c${columnIndex}`}
      className={classNames(s.cellContainer, containerStyles, focusedClass, {
        [s.cellContainerSelected]: isCellSelected,
      })}
      // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
      onMouseOver={handleHover}
      aria-colindex={columnIndex + 1}
      {...(buttonProps && !isHeader ? buttonProps : {})}
    >
      {hasDueDateException && (
        <div className={s.dueDateIconContainer}>
          <MLTooltip content="Due date exception provided">
            <MLIcon
              className={s.infoIcon}
              type="info_outline"
              role="presentation"
            />
          </MLTooltip>
        </div>
      )}
      {isCellSelected && <GridCellBorder />}
      {buttonWrapper(content)}
    </div>
  );
};

GridCell.propTypes = {
  columnIndex: PropTypes.number,
  rowIndex: PropTypes.number,
  scrollToColumn: PropTypes.number,
  scrollToRow: PropTypes.number,
  rowCount: PropTypes.number,
  leftGridColumnCount: PropTypes.number,
  style: PropTypes.object,
  cellData: PropTypes.object,
  courseExceptions: PropTypes.array,
  collapsedCategoryIds: PropTypes.array,
  showPercent: PropTypes.bool,
  hoveredColumnIndex: PropTypes.number,
  hoveredRowIndex: PropTypes.number,
  handleCellHover: PropTypes.func,
  showGradeOverrideDialog: PropTypes.func,
  onSelectCell: PropTypes.func,
  dispatch: PropTypes.func,
  gradingType: PropTypes.number,
  roundPercentType: PropTypes.number,
  user: PropTypes.object,
  studentSortKey: PropTypes.string,
  studentSortDirection: PropTypes.string,
  courseId: PropTypes.string.isRequired,
  sortColumnHistory: PropTypes.arrayOf(PropTypes.string),
  getLaunchUrl: PropTypes.func,
  handleLocalLaunch: PropTypes.func,
  hasStudentReport: PropTypes.bool,
  categories: PropTypes.array,
};

const mapStateToProps = (state) => {
  const history = state.gradebook.sortColumnHistory;
  const opts = {
    sortColumn: state.gradebook.sortColumn,
    sortDirection: state.gradebook.sortDirection,
    iconSortDirection: state.gradebook.icon,
  };
  const { studentSortKey, studentSortDirection } = getSortProperties(opts);
  opts.studentSortKey = studentSortKey;
  opts.studentSortDirection = studentSortDirection;
  if (studentSortKey && history.length === 1) {
    history.push(studentSortKey);
  }
  opts.sortColumnHistory = history;
  opts.categories = get(state, 'gradebook.categories', []);
  return opts;
};

export default connect(mapStateToProps)(GridCell);
